import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Building2,
  Ticket,
  Trophy,
  Users,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  Search,
  ChevronDown,
} from 'lucide-react';
import { cn, Avatar, Button } from '@raffle/ui';
import { useAuth } from '../../providers/AuthProvider';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
  { icon: Building2, label: 'Properties', path: '/properties' },
  { icon: Ticket, label: 'Tickets', path: '/tickets' },
  { icon: Trophy, label: 'Draws', path: '/draws' },
  { icon: Users, label: 'Users', path: '/users' },
  { icon: MessageSquare, label: 'Support', path: '/support' },
  { icon: Settings, label: 'Settings', path: '/settings' },
];

export const AdminLayout: React.FC = () => {
  const { user, logout, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-0 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const currentPage = navItems.find((item) => 
    item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path)
  );

  return (
    <div className="min-h-screen bg-surface-0 flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:w-[280px] lg:flex-col lg:fixed lg:inset-y-0 bg-surface-1 border-r border-white/5">
        {/* Logo */}
        <div className="h-[72px] flex items-center px-6 border-b border-white/5">
          {/* Nigerian Flag Accent */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#008751] via-white to-[#008751]" />
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-vault bg-gradient-to-br from-[#008751] to-gold-500 flex items-center justify-center relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#008751] via-white to-[#008751]" />
              <span className="text-white font-bold text-sm">NV</span>
            </div>
            <div>
              <span className="font-display text-lg font-bold text-text-primary">
                NaijaVault
              </span>
              <span className="block text-xs text-[#008751] font-medium">Admin Portal</span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = item.path === '/' 
              ? location.pathname === '/' 
              : location.pathname.startsWith(item.path);

            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 rounded-vault transition-colors',
                  isActive
                    ? 'bg-gold-500/10 text-gold-500'
                    : 'text-text-secondary hover:bg-surface-2 hover:text-text-primary'
                )}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-white/5">
          <button
            onClick={logout}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-vault text-text-secondary hover:bg-surface-2 hover:text-text-primary transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setIsSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 30, stiffness: 400 }}
              className="fixed inset-y-0 left-0 w-[280px] bg-surface-1 border-r border-white/5 z-50 lg:hidden"
            >
              {/* Logo */}
              <div className="h-[72px] flex items-center justify-between px-6 border-b border-white/5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-vault bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center">
                    <span className="text-black font-display font-bold text-lg">R</span>
                  </div>
                  <span className="font-display text-lg font-bold text-text-primary">
                    Admin
                  </span>
                </div>
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="p-2 rounded-vault bg-surface-2 text-text-muted"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Navigation */}
              <nav className="flex-1 px-4 py-6 space-y-1">
                {navItems.map((item) => {
                  const isActive = item.path === '/' 
                    ? location.pathname === '/' 
                    : location.pathname.startsWith(item.path);

                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsSidebarOpen(false)}
                      className={cn(
                        'flex items-center gap-3 px-4 py-3 rounded-vault transition-colors',
                        isActive
                          ? 'bg-gold-500/10 text-gold-500'
                          : 'text-text-secondary hover:bg-surface-2 hover:text-text-primary'
                      )}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </NavLink>
                  );
                })}
              </nav>

              {/* User Section */}
              <div className="p-4 border-t border-white/5">
                <button
                  onClick={() => {
                    logout();
                    setIsSidebarOpen(false);
                  }}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-vault text-text-secondary hover:bg-surface-2 hover:text-text-primary transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Sign Out</span>
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 lg:ml-[280px]">
        {/* Header */}
        <header className="sticky top-0 z-30 h-[72px] bg-surface-0/80 backdrop-blur-lg border-b border-white/5">
          <div className="h-full px-4 lg:px-8 flex items-center justify-between gap-4">
            {/* Left side */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="lg:hidden p-2 rounded-vault bg-surface-2 text-text-muted"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="font-display text-lg font-semibold text-text-primary">
                  {currentPage?.label || 'Admin'}
                </h1>
              </div>
            </div>

            {/* Search (Desktop) */}
            <div className="hidden md:flex flex-1 max-w-md mx-4">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  type="search"
                  placeholder="Search..."
                  className="w-full h-10 pl-10 pr-4 bg-surface-2 border border-white/10 rounded-vault text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold-500/50"
                />
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {/* Notifications */}
              <button className="relative p-2 rounded-vault bg-surface-2 text-text-muted hover:text-text-primary transition-colors">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-vault-error rounded-full" />
              </button>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 p-1.5 pr-3 rounded-vault bg-surface-2 hover:bg-surface-3 transition-colors"
                >
                  <Avatar name={user?.fullName || 'Admin'} size="sm" />
                  <span className="hidden sm:block text-sm font-medium text-text-primary">
                    {user?.fullName}
                  </span>
                  <ChevronDown className="w-4 h-4 text-text-muted" />
                </button>

                <AnimatePresence>
                  {showUserMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowUserMenu(false)}
                      />
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        className="absolute right-0 top-full mt-2 w-48 py-2 bg-surface-2 border border-white/10 rounded-vault-lg shadow-xl z-50"
                      >
                        <div className="px-3 py-2 border-b border-white/5">
                          <p className="text-sm font-medium text-text-primary">{user?.fullName}</p>
                          <p className="text-xs text-text-muted">{user?.email}</p>
                        </div>
                        <button
                          onClick={() => {
                            logout();
                            setShowUserMenu(false);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:bg-surface-3 hover:text-text-primary transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
